import React from "react";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";

/**
 * PercentChange Component
 *
 * A flexible component for displaying percentage changes with standard grey styling.
 * Supports multiple display formats including custom templates and internationalization.
 *
 * @example Basic usage:
 * ```tsx
 * <PercentChange value={5.2} />
 * // Renders: "+5.2% from last cycle"
 * ```
 *
 * @example Custom template with interpolation:
 * ```tsx
 * <PercentChange
 *   value={-3.1}
 *   customTemplate="vs {{percentChange}} last cycle"
 * />
 * // Renders: "vs -3.1% last cycle"
 * ```
 *
 * @example Using custom translation keys:
 * ```tsx
 * <PercentChange
 *   value={2.5}
 *   zeroChangeKey="myCustom.zeroMessage"
 *   customTemplate="{{percentChange}} from last period"
 * />
 * ```
 *
 * @param value - The percentage change value (e.g., 5.2 for +5.2%)
 * @param prevCount - Previous period count; if 0, shows zero change message
 * @param className - Additional CSS classes for styling customization
 * @param zeroChangeKey - Full translation key for zero change message (default: "dataCell.zeroChange.vsZeroLastCycle")
 * @param customTemplate - Custom template string with {{percentChange}} placeholder
 *                         for advanced formatting.
 *
 * @remarks
 * Translation keys can be any valid translation path. The component uses the keys
 * directly without forcing any prefix, allowing for flexible translation organization.
 *
 * Common usage patterns:
 * - zeroChangeKey: "dataCell.zeroChange.vsZeroLastCycle", "myCustom.zeroMessage", etc.
 * - suffixKey: "dataCell.change.fromLastCycle", "myCustom.changeMessage", etc.
 *
 * The component maintains backwards compatibility with existing translation paths
 * for gradual migration of legacy code.
 *
 * Zero change handling: When prevCount is 0, displays a special message instead
 * of attempting to calculate percentage change (which would be undefined).
 */
interface PercentChangeProps {
  value?: number | null;
  prevCount?: number | null;
  className?: string;
  zeroChangeKey?: string; // Key for zero change message
  noDataKey?: string; // Key for null|undefined prevCount message
  customTemplate?: string; // Custom template string with {{percentChange}} placeholder
}

export const PercentChange: React.FC<PercentChangeProps> = ({
  value,
  prevCount,
  className,
  zeroChangeKey = "metrics.comparisons.vsZeroLastCycle",
  noDataKey = "metrics.comparisons.noDataLastCycle",
  customTemplate,
}) => {
  const { t } = useTranslation();

  // Special case: No current value
  if (value === undefined || value === null) {
    return (
      <span className={clsx("text-sm text-muted-foreground", className)}>
        {t("metrics.comparisons.noData", "data unavailable")}
      </span>
    );
  }

  // Special case: previous period had 0, so percent change is not meaningful.
  if (prevCount === 0) {
    const zeroChangeText = t(zeroChangeKey, {
      defaultValue: t(
        "metrics.comparisons.vsZeroLastCycle",
        "vs. 0 last cycle",
      ),
    });

    return (
      <span className={clsx("text-sm text-muted-foreground", className)}>
        {zeroChangeText}
      </span>
    );
  }

  if (prevCount === null || prevCount === undefined) {
    const noDataText = t(noDataKey, {
      defaultValue: t(
        "metrics.comparisons.noDataLastCycle",
        "historical data unavailable",
      ),
    });

    return (
      <span className={clsx("text-sm text-muted-foreground", className)}>
        {noDataText}
      </span>
    );
  }

  const finalVal = Math.round(value);
  const sign = finalVal >= 0 ? "+" : "";
  const formattedValue = `${sign}${finalVal}%`;

  const percentageSpan = (
    <span
      className={clsx("text-sm font-medium text-muted-foreground", className)}
    >
      {formattedValue}
    </span>
  );

  if (customTemplate) {
    const templateWithValue = customTemplate.replace(
      "{{percentChange}}",
      formattedValue,
    );
    return (
      <span className={clsx("text-sm text-muted-foreground", className)}>
        {templateWithValue}
      </span>
    );
  }

  // Catch-all
  return <span className="flex items-center gap-1">{percentageSpan}</span>;
};
